import {
  DrawerContent,
  LoadingCircle,
  FormGroup,
  Button,
  Checkbox,
  ButtonVariant,
  DrawerActions,
} from "@industriousoffice/react-components";
import { useTranslation } from "react-i18next";
import CardValidator from "card-validator";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  Account,
  AccountSignature,
  InvoicingSystemTypes,
  Location,
  ZuoraPaymentMethodTypes,
} from "@/api/opb/opbTypes";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { PaymentDrawerTitle as DrawerTitle } from "./PaymentDrawerTitle";
import { PaymentFormInput as FormInput } from "./PaymentFormInput";
import { PaymentDrawer as Drawer } from "./PaymentDrawer";
import {
  PaymentMethodSchema,
  PaymentMethodSchemaType,
} from "../schemas/PaymentMethodSchema";
import { encrypt } from "../utils/encrypt";
import { FormPaymentMethodType, ZuoraCreditCardTypes } from "../types";
import StripePaymentMethodDrawerForm from "./StripePaymentMethodDrawerForm";
import FormCardNumberInput from "./FormCardNumberInput";
import FormExpirationDateInput from "./FormExpirationDateInput";

import { usePaymentSetup } from "../usePaymentSetup";

import { CardFeeNotice } from "./CardFeeNotice";

interface AddPaymentMethodDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  accountSignature?: AccountSignature;
  account?: Account;
  accountLocation?: Location;
  isFirstPaymentMethod?: boolean;
  callbackData?: object;
  redirectUrl?: string;
}

const defaultFormData: PaymentMethodSchemaType = {
  cardNumber: "",
  expirationDate: "",
  cvv: "",
  field_creditCardHolderName: "",
  field_passthrough4: "", // nickname
  field_passthrough5: "", // redirect url
  field_passthrough6: "", // callback data
};

export function AddPaymentMethodDrawer({
  isOpen,
  onClose,
  isLoading = false,
  accountSignature,
  account,
  accountLocation,
  isFirstPaymentMethod = false,
  redirectUrl,
  callbackData,
}: AddPaymentMethodDrawerProps) {
  const { t } = useTranslation("paymentMethods");

  const [isDefaultPaymentChecked, setIsDefaultPaymentChecked] =
    useState(isFirstPaymentMethod);

  const {
    control,
    reset,
    trigger,
    watch,
    formState: { isDirty, isValid },
  } = useForm<PaymentMethodSchemaType>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: defaultFormData,
    resolver: yupResolver(PaymentMethodSchema),
  });

  useEffect(() => {
    setIsDefaultPaymentChecked(isFirstPaymentMethod);
  }, [isFirstPaymentMethod]);

  const formValues = watch();
  const creditCardValues = useMemo(() => {
    if (isDirty) {
      const [creditCardExpirationMonth, creditCardExpirationYear] =
        formValues.expirationDate.split("/");
      const creditCardNumber = formValues.cardNumber.split(" ").join("");
      const creditCard = CardValidator.number(creditCardNumber);
      const creditCardType = creditCard?.card?.type;
      const valuesToEncrypt = `#${creditCardNumber}#${formValues.cvv}#${creditCardExpirationMonth}#${creditCardExpirationYear}`;

      try {
        const encryptedValues = encrypt(
          valuesToEncrypt,
          accountSignature?.publicKey || ""
        );

        return {
          creditCardType: creditCardType
            ? ZuoraCreditCardTypes[creditCardType]
            : undefined,
          encryptedValues,
        };
      } catch (e) {
        return null;
      }
    }

    return null;
  }, [
    isDirty,
    formValues.cardNumber,
    formValues.cvv,
    formValues.expirationDate,
    accountSignature?.publicKey,
  ]);

  const encryptionReady = useMemo(() => {
    if (accountSignature?.publicKey == null) return false;
    try {
      return (
        encrypt("secure-setup-check", accountSignature.publicKey).length > 0
      );
    } catch {
      return false;
    }
  }, [accountSignature?.publicKey]);
  const setup = usePaymentSetup({
    requiresDefaultConfirmation: Boolean(
      account?.autopay && isDefaultPaymentChecked && !isFirstPaymentMethod
    ),
    isOpen,
    isDirty: isDirty || isDefaultPaymentChecked !== isFirstPaymentMethod,
    isValid: isValid && accountSignature != null && creditCardValues != null,
    validate: async () =>
      (await trigger(undefined, { shouldFocus: true })) &&
      creditCardValues != null &&
      encryptionReady,
    onClose,
    onReset: () => {
      reset(defaultFormData);
      setIsDefaultPaymentChecked(isFirstPaymentMethod);
    },
  });
  return (
    <Drawer className="z-10" isOpen={isOpen} onClose={setup.requestClose}>
      {setup.discardDialog}
      <DrawerTitle
        className="pb-4"
        title={t("Add a new card")}
        onClose={setup.requestClose}
      />
      {account?.invoicingSystemType === InvoicingSystemTypes.StripeBilling ? (
        <StripePaymentMethodDrawerForm
          accountId={account?._id}
          paymentType={ZuoraPaymentMethodTypes.CreditCard}
          currencyIsoCode={accountLocation?.currencyCode}
          isFirstPaymentMethod={isFirstPaymentMethod}
          isDefaultPaymentChecked={isDefaultPaymentChecked}
          setIsDefaultPaymentChecked={setIsDefaultPaymentChecked}
        />
      ) : (
        <form
          name="CcPayment2Form"
          action={accountSignature?.paymentPageUrl}
          method="post"
          onSubmit={setup.onSubmit}
          noValidate
          className="payment-add-form"
          aria-busy={setup.isSubmitting}
          id="add_cc_form"
        >
          <DrawerContent>
            {isLoading ? (
              <div className="flex items-center justify-center pt-4">
                <LoadingCircle spinnerSize={30} spinnerColor="#013e3f" />
              </div>
            ) : (
              <>
                <input type="hidden" name="method" value="submitPage" />
                <input
                  type="hidden"
                  name="id"
                  value={accountSignature?.paymentPageId || ""}
                />
                <input
                  type="hidden"
                  name="tenantId"
                  value={accountSignature?.tenantId}
                />
                <input
                  type="hidden"
                  name="token"
                  value={accountSignature?.token}
                />
                <input
                  type="hidden"
                  name="signature"
                  value={accountSignature?.signature}
                />
                <input
                  type="hidden"
                  name="host"
                  value={window.location.origin}
                />
                <input
                  type="hidden"
                  name="field_accountId"
                  value={account?.zuoraId}
                />
                <input
                  type="hidden"
                  name="paymentGateway"
                  value={account?.paymentGateway}
                />
                <input
                  type="hidden"
                  name="field_key"
                  value={accountSignature?.publicKey}
                />
                <input
                  type="hidden"
                  name="encrypted_fields"
                  value="#field_creditCardNumber#field_cardSecurityCode#field_creditCardExpirationMonth#field_creditCardExpirationYear"
                />
                <input
                  type="hidden"
                  name="encrypted_values"
                  value={creditCardValues?.encryptedValues}
                />
                <input type="hidden" name="field_style" value="iframe" />
                <input
                  type="hidden"
                  name="field_passthrough1"
                  value={account?._id}
                />
                <input
                  type="hidden"
                  name="field_passthrough2"
                  value={FormPaymentMethodType.CREDIT_CARD}
                />
                <input
                  type="hidden"
                  name="field_passthrough3"
                  value={isDefaultPaymentChecked ? "true" : "false"}
                />
                <input
                  type="hidden"
                  name="field_creditCardType"
                  value={creditCardValues?.creditCardType}
                />
                <input
                  type="hidden"
                  name="field_passthrough5"
                  value={redirectUrl}
                />
                <input
                  type="hidden"
                  name="field_passthrough6"
                  value={JSON.stringify(callbackData)}
                />
                <FormGroup className="payment-form-group">
                  <FormCardNumberInput
                    control={control}
                    name="cardNumber"
                    rules={{ required: true }}
                    label={t("Card number")}
                    placeholder="1234 1234 1234 1234"
                    isRequired
                    data-testid="add_payment_method-card_number-input"
                  />
                </FormGroup>
                <div className="grid grid-cols-2 gap-4">
                  <FormGroup className="payment-form-group">
                    <FormExpirationDateInput
                      name="expirationDate"
                      control={control}
                      rules={{
                        required: true,
                      }}
                      label={t("Expiration date")}
                      isRequired
                      placeholder="MM/YYYY"
                      data-testid="add_payment_method-expiration_date-input"
                    />
                  </FormGroup>
                  <FormGroup className="payment-form-group">
                    <FormInput
                      control={control}
                      name="cvv"
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      autoComplete="cc-csc"
                      rules={{ required: true }}
                      label={t("CVV/CVC")}
                      placeholder="555"
                      isRequired
                      data-testid="add_payment_method-cvv-input"
                    />
                  </FormGroup>
                </div>
                <FormGroup className="payment-form-group">
                  <FormInput
                    control={control}
                    name="field_creditCardHolderName"
                    rules={{ required: true }}
                    label={t("Name on card")}
                    placeholder=""
                    isRequired
                    data-testid="add_payment_method-name_on_card-input"
                  />
                </FormGroup>
                <FormGroup className="payment-form-group">
                  <FormInput
                    control={control}
                    name="field_passthrough4"
                    label={t("Nickname")}
                    placeholder=""
                    data-testid="add_payment_method-nickname-input"
                  />
                </FormGroup>
              </>
            )}
            {!encryptionReady && accountSignature != null && !isLoading && (
              <p role="alert" className="payment-error">
                {t(
                  "The secure payment form could not be initialized. Please reload and try again."
                )}
              </p>
            )}
            <CardFeeNotice location={accountLocation} />
          </DrawerContent>
          <DrawerActions>
            <div className="w-full grow">
              <Checkbox
                label={t("Set as default payment method")}
                subtitle={t(
                  isFirstPaymentMethod
                    ? "Your first payment method is automatically the default. Auto-Pay will stay unchanged."
                    : "Used for Auto-Pay when it is on. This does not turn Auto-Pay on."
                )}
                className="text-base font-normal font-hanken"
                onChange={() =>
                  setIsDefaultPaymentChecked(!isDefaultPaymentChecked)
                }
                checked={isDefaultPaymentChecked}
                data-testid="add_payment_method-default-checkbox"
                isDisabled={isFirstPaymentMethod}
              />
              <div className="payment-drawer-buttons">
                <Button
                  type="button"
                  variant={ButtonVariant.Outline}
                  isDisabled={setup.isSubmitting}
                  onClick={setup.requestClose}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  className="w-full py-5 font-hanken sm:w-56"
                  variant={ButtonVariant.Primary}
                  type="submit"
                  isLoading={setup.isSubmitting}
                  isDisabled={
                    setup.isSubmitting ||
                    !encryptionReady ||
                    accountSignature == null
                  }
                >
                  {t("Add Payment Method")}
                </Button>
              </div>
            </div>
          </DrawerActions>
        </form>
      )}
    </Drawer>
  );
}
